/**
 * Events module permissions.
 *
 * Capability = Admin or EventsAdmin (all Event.* rights, PG identity/004), or the matching
 * `Event.*` right (rights can be granted per user without a role). The "Events Access" role
 * carries the read rights (Event.View, Event.Type.View, Event.Source.View), so it opens every
 * read surface by default.
 *
 * Stage 1B: the "Events Coordinator" role (PG identity/005) carries Event.View and
 * Event.Link.Manage only. The coordinator of an event may also manage that event's links (checked
 * per event with `canManageEventLinks`).
 *
 * Stage 2 (PG identity/006): Event.Playbook.Manage (playbooks) and Event.Plan.Approve (approve a
 * plan, which creates work in other apps as the approver). The coordinator of an event may draft,
 * edit and approve that event's plans without either right (`canEditEventPlan`,
 * `canApproveEventPlan`).
 *
 * Stage 3 (PG identity/007): Event.AI.Use ("Suggest with AI" on an event's Connected tab). Admin
 * and EventsAdmin pass; being the event's coordinator is not enough.
 *
 * The UI only hides what the user cannot do; the API enforces the same rules.
 */

import { useCallback, useMemo } from 'react';
import { EVENT_RIGHTS, EVENT_ROLES } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '../useEnrichedAuth';

export { EVENT_RIGHTS, EVENT_ROLES };

/** Roles that open the Events module (the domain gate). */
export const EVENTS_MODULE_ROLES: string[] = [
  EVENT_ROLES.Admin,
  EVENT_ROLES.EventsAdmin,
  EVENT_ROLES.EventsAccess,
  EVENT_ROLES.EventsCoordinator,
];

/** True when a role list opens the Events app (used to warn before naming a coordinator). */
export function rolesOpenEvents(roles: readonly string[] | null | undefined): boolean {
  const set = new Set((roles ?? []).map((r) => r.toLowerCase()));
  return EVENTS_MODULE_ROLES.some((r) => set.has(r.toLowerCase()));
}

export function useEventsPermissions() {
  const { user, hasRole, hasPermission } = useEnrichedAuth();

  const isAdmin = hasRole(EVENT_ROLES.Admin) || hasRole('admin');
  const isEventsAdmin = isAdmin || hasRole(EVENT_ROLES.EventsAdmin);
  const isReader = hasRole(EVENT_ROLES.EventsAccess);
  /** Event.View and Event.Link.Manage only (not the type and source read rights). */
  const isCoordinatorRole = hasRole(EVENT_ROLES.EventsCoordinator);

  /** Admin / EventsAdmin, or (for read rights) the "Events Access" role, or the explicit right. */
  const can = useCallback(
    (right: string, readerDefault = false) => isEventsAdmin || (readerDefault && isReader) || hasPermission(right),
    [isEventsAdmin, isReader, hasPermission]
  );

  const perms = useMemo(() => {
    const R = EVENT_RIGHTS;
    return {
      // Events
      canView: can(R.View, true) || isCoordinatorRole,
      canCreate: can(R.Create),
      canUpdate: can(R.Update),
      /** Soft delete. */
      canDelete: can(R.Delete),
      /** Resolve events held for review. */
      canReview: can(R.Review),
      // Types
      canViewTypes: can(R.TypeView, true),
      canCreateType: can(R.TypeCreate),
      canUpdateType: can(R.TypeUpdate),
      canDeleteType: can(R.TypeDelete),
      // Sources + mappings
      canViewSources: can(R.SourceView, true),
      canManageSources: can(R.SourceManage),
      canSync: can(R.SourceSync),
      canManageMappings: can(R.MappingManage),
      // Stage 1B: links, tags, rules and learning
      /** Add, approve, dismiss and remove links on any event, and set coordinators. */
      canManageLinks: can(R.LinkManage) || isCoordinatorRole,
      /** Create and edit manual tags and set them on events. */
      canManageTags: can(R.TagManage),
      canManageRules: can(R.RuleManage),
      canReviewLearning: can(R.LearningReview),
      // Stage 2: playbooks and plans
      /** Create, edit and delete playbooks. */
      canManagePlaybooks: can(R.PlaybookManage),
      /** Approve plans on any event (creates work as the approver), retry and skip items. */
      canApprovePlans: can(R.PlanApprove),
      // Stage 3: AI
      /** Ask AI for an event's link and playbook suggestions. */
      canUseAi: can(R.AiUse),
    };
  }, [can, isCoordinatorRole]);

  const userId = user?.id as string | undefined;

  /** May manage this event's links: the right, or being its coordinator. */
  const canManageEventLinks = useCallback(
    (coordinatorUserId?: string | null) =>
      perms.canManageLinks || (!!userId && !!coordinatorUserId && coordinatorUserId.toLowerCase() === userId.toLowerCase()),
    [perms.canManageLinks, userId]
  );

  const isCoordinatorOf = useCallback(
    (coordinatorUserId?: string | null) => !!userId && !!coordinatorUserId && coordinatorUserId.toLowerCase() === userId.toLowerCase(),
    [userId]
  );

  /** May draft and edit this event's plans: Event.Plan.Approve, Event.Playbook.Manage or being its coordinator. */
  const canEditEventPlan = useCallback(
    (coordinatorUserId?: string | null) => perms.canApprovePlans || perms.canManagePlaybooks || isCoordinatorOf(coordinatorUserId),
    [perms.canApprovePlans, perms.canManagePlaybooks, isCoordinatorOf]
  );

  /** May approve this event's plans and retry or skip items: Event.Plan.Approve or being its coordinator. */
  const canApproveEventPlan = useCallback(
    (coordinatorUserId?: string | null) => perms.canApprovePlans || isCoordinatorOf(coordinatorUserId),
    [perms.canApprovePlans, isCoordinatorOf]
  );

  return {
    ...perms,
    isAdmin,
    isEventsAdmin,
    isReader,
    isCoordinatorRole,
    /** May open the module at all. */
    canOpenModule: perms.canView,
    canManageEventLinks,
    isCoordinatorOf,
    canEditEventPlan,
    canApproveEventPlan,
    userId,
  };
}

export type EventsPermissions = ReturnType<typeof useEventsPermissions>;
